import { AlbumSummaryWithArtist } from './AlbumSummaryWithArtist';
import { ArtistSummary } from './ArtistSummary';
import { ArtistSummaryWithAlbums } from './ArtistSummaryWithAlbums';

export class AlbumsCollection {
  albums: AlbumSummaryWithArtist[];
  // TODO: consider sorting dynamically, or at least delaying the sort
  // until the first request comes in for the sort in that order.
  albumsSortedByArtistAlbumCount: AlbumSummaryWithArtist[];
  albumsSortedByReviewDate: AlbumSummaryWithArtist[];
  albumsSortedByRatingAscending: AlbumSummaryWithArtist[];
  albumsSortedByRatingDescending: AlbumSummaryWithArtist[];
  albumsSortedByYearAscending: AlbumSummaryWithArtist[];
  albumsSortedByYearDescending: AlbumSummaryWithArtist[];

  constructor() {
    this.albums = new Array<AlbumSummaryWithArtist>();
  }

  deserializeArtistSummaryWithAlbums(artistSummaryWithAlbumsCollection: ArtistSummaryWithAlbums[]): void {
    for (const artistSummaryWithAlbums of artistSummaryWithAlbumsCollection) {
      const artistSummary = new ArtistSummary();
      artistSummary.deserialize(artistSummaryWithAlbums);
      let albumSortPosition: number = 0;
      for (const albumSummary of artistSummaryWithAlbums.albums) {
        const albumSummaryWithArtist = new AlbumSummaryWithArtist(artistSummary, albumSortPosition);
        albumSummaryWithArtist.deserialize(albumSummary);
        this.albums.push(albumSummaryWithArtist);
        albumSortPosition++;
      }
    }
    this.buildAlbumsSortedByArtistAlbumCount();
    this.buildAlbumsSortedByReviewDate();
    this.buildAlbumsSortedByRatingAscending();
    this.buildAlbumsSortedByRatingDescending();
    this.buildAlbumsSortedByYearAscending();
    this.buildAlbumsSortedByYearDescending();
  }

  buildAlbumsSortedByArtistAlbumCount(): void {
    const sortedAlbums: Array<AlbumSummaryWithArtist> = this.albums.slice(0);
    sortedAlbums.sort(
      (leftSide: AlbumSummaryWithArtist,
      rightSide: AlbumSummaryWithArtist) => {
      if (leftSide.artist.albumCount > rightSide.artist.albumCount) {
        return -1;
      } else if (leftSide.artist.albumCount < rightSide.artist.albumCount) {
        return 1;
      } else {
        return this.compareArtistNameAndReleaseDate(leftSide, rightSide);
      }
    });
    this.albumsSortedByArtistAlbumCount = sortedAlbums;
  }

  buildAlbumsSortedByReviewDate(): void {
    const sortedAlbums: Array<AlbumSummaryWithArtist> = this.albums.slice(0);
    sortedAlbums.sort(
      (leftSide: AlbumSummaryWithArtist,
      rightSide: AlbumSummaryWithArtist) => {
      if (leftSide.reviewDate > rightSide.reviewDate) {
        return -1;
      } else if (leftSide.reviewDate < rightSide.reviewDate) {
        return 1;
      } else {
        return this.compareArtistNameAndReleaseDate(leftSide, rightSide);
      }
    });
    this.albumsSortedByReviewDate = sortedAlbums;
  }

  buildAlbumsSortedByRatingAscending(): void {
    const sortedAlbums: Array<AlbumSummaryWithArtist> = this.albums.slice(0);
    sortedAlbums.sort(
      (leftSide: AlbumSummaryWithArtist,
      rightSide: AlbumSummaryWithArtist) => {
      if (leftSide.rating > rightSide.rating) {
        return 1;
      } else if (leftSide.rating < rightSide.rating) {
        return -1;
      } else {
        return this.compareArtistNameAndReleaseDate(leftSide, rightSide);
      }
    });
    this.albumsSortedByRatingAscending = sortedAlbums;
  }  

  buildAlbumsSortedByRatingDescending(): void {
    const sortedAlbums: Array<AlbumSummaryWithArtist> = this.albums.slice(0);
    sortedAlbums.sort(
      (leftSide: AlbumSummaryWithArtist,
      rightSide: AlbumSummaryWithArtist) => {
      if (leftSide.rating < rightSide.rating) {
        return 1;
      } else if (leftSide.rating > rightSide.rating) {
        return -1;
      } else {
        return this.compareArtistNameAndReleaseDate(leftSide, rightSide);
      }
    });
    this.albumsSortedByRatingDescending = sortedAlbums;
  }  

  buildAlbumsSortedByYearAscending(): void {
    const sortedAlbums: Array<AlbumSummaryWithArtist> = this.albums.slice(0);
    sortedAlbums.sort(
      (leftSide: AlbumSummaryWithArtist,
      rightSide: AlbumSummaryWithArtist) => {
      if (leftSide.year > rightSide.year) {
        return 1;
      } else if (leftSide.year < rightSide.year) {
        return -1;
      } else {
        return this.compareArtistNameAndReleaseDate(leftSide, rightSide);
      }
    });
    this.albumsSortedByYearAscending = sortedAlbums;
  }    

  buildAlbumsSortedByYearDescending(): void {
    const sortedAlbums: Array<AlbumSummaryWithArtist> = this.albums.slice(0);
    sortedAlbums.sort(
      (leftSide: AlbumSummaryWithArtist,
      rightSide: AlbumSummaryWithArtist) => {
      if (leftSide.year < rightSide.year) {
        return 1;
      } else if (leftSide.year > rightSide.year) {
        return -1;
      } else {
        return this.compareArtistNameAndReleaseDate(leftSide, rightSide);
      }
    });
    this.albumsSortedByYearDescending = sortedAlbums;
  }    

  // will compare albums first by artist name A-Z then by release date ascending
  compareArtistNameAndReleaseDate(leftSide: AlbumSummaryWithArtist, rightSide: AlbumSummaryWithArtist) {
    // sort by artist name A-Z, using sortPosition which is the appropriate ordering
    const artistNameComparison: number = leftSide.artist.sortPosition - rightSide.artist.sortPosition;
    if (artistNameComparison !== 0) {
        return artistNameComparison;
    }
    // sort by album release date
    return leftSide.sortPosition - rightSide.sortPosition;
  }

  getAlbumsSortedAlphabeticallyByArtistNameThenChronologically(): Array<AlbumSummaryWithArtist> {
    return this.albums;
  }

  getAlbumsSortedByArtistAlbumCountThenChronologically(): Array<AlbumSummaryWithArtist> {
    return this.albumsSortedByArtistAlbumCount;
  }

  getAlbumsSortedByReviewDate(): Array<AlbumSummaryWithArtist> {
    return this.albumsSortedByReviewDate;
  }

  getAlbumsSortedByRatingAscending(): Array<AlbumSummaryWithArtist> {
    return this.albumsSortedByRatingAscending;
  }

  getAlbumsSortedByRatingDescending(): Array<AlbumSummaryWithArtist> {
    return this.albumsSortedByRatingDescending;
  }

  getAlbumsSortedByYearAscending(): Array<AlbumSummaryWithArtist> {
    return this.albumsSortedByYearAscending;
  }

  getAlbumsSortedByYearDescending(): Array<AlbumSummaryWithArtist> {
    return this.albumsSortedByYearDescending;
  }

}
